import { css, Icon, MAGNETIC_TOKENS } from '@appd/uik';
import type { ts } from '@appd/utils';
import { many } from '@appd/utils';
import { isArray, pick } from 'lodash';
import { format } from 'url';
import { buildObserveUrl } from '../../../utils';
import { useTemplateStore } from '../DashboardViewer/hooks/TemplateProvider';
import { SectionCardPropsEntry } from './SectionCard';
import { SectionCardEntryTag } from './SectionCardEntryTag';

export interface SectionCardEntryProps {
  indentation: number;
  entry: SectionCardPropsEntry;
  onEntrySelect?: (entry: SectionCardPropsEntry) => void;
  enableDrilldown?: boolean;
  className?: ts.OrMany<Parameters<css>[0]>;
}

export interface SectionCardEntryPropsHealthGroup {
  health: string;
  count: number;
}

function createHref(entityType: string, globals: unknown, healthFilter?: string): string {
  const query = pick((globals as Record<string, unknown>).variables, ['since', 'until']);
  const filter = healthFilter ? `isActive=true&&attributes(status) IN [${healthFilter}]` : 'isActive=true';

  Object.assign(query, { filter });

  return format(buildObserveUrl(entityType, query));
}

export function SectionCardEntry({
  indentation,
  onEntrySelect,
  entry,
  className,
  enableDrilldown = false,
}: // onExpandToggle
SectionCardEntryProps): JSX.Element {
  const { entityType, healthGroups, iconName, displayName } = entry;

  const totalCount = isArray(healthGroups) ? healthGroups.reduce((a, { count }) => a + count, 0) : 0;

  const onClick = (event: MouseEvent | KeyboardEvent) => {
    event.preventDefault();
    if (onEntrySelect) {
      onEntrySelect(entry);
    }
  };

  const [globals] = useTemplateStore((store: unknown) => (store as Record<string, unknown>).globals);

  // @ts-ignore
  // @ts-ignore
  return (
    <div
      className={css(
        {
          borderBottom: `2px solid ${MAGNETIC_TOKENS.COLORS.CONTROL_BORDER_WEAK}`,
          display: 'flex',
          flexWrap: 'wrap',
          gap: MAGNETIC_TOKENS.SPACING[2],
          alignItems: 'center',
          paddingBlock: MAGNETIC_TOKENS.SPACING[2],
          paddingInline: MAGNETIC_TOKENS.SPACING[3],
          boxSizing: 'border-box',
          transition: 'all 0.2s',
          ':hover': { backgroundColor: MAGNETIC_TOKENS.COLORS.CONTROL_BG_HOVER },
        },
        ...many(className),
      )}
    >
      {enableDrilldown ? (
        <a
          href={createHref(entityType, globals)}
          className={css(MAGNETIC_TOKENS.TYPOGRAPHY.ELLIPSES.END, {
            marginLeft: MAGNETIC_TOKENS.SPACING[3] * indentation,
            flex: '0 0 auto',
            maxWidth: '100%',
            fontSize: '14px',
            lineHeight: '20px',
            color: MAGNETIC_TOKENS.COLORS.DEFAULT_TEXT,
            textDecoration: 'none',
            ':hover': {
              color: MAGNETIC_TOKENS.COLORS.INTERACT_TEXT,
              '> svg path': { fill: MAGNETIC_TOKENS.COLORS.INTERACT_TEXT },
              '> .count': { color: MAGNETIC_TOKENS.COLORS.INTERACT_TEXT },
            },
          })}
          // @ts-ignore
          onClick={onClick}
        >
          <Icon type={iconName} size="SMALL" className={{ verticalAlign: 'middle', marginRight: '0.5em' }} />
          {displayName}
          <span
            className={css('count', {
              marginLeft: MAGNETIC_TOKENS.SPACING[2],
              fontSize: MAGNETIC_TOKENS.TYPOGRAPHY.SIZE_P4,
              fontWeight: MAGNETIC_TOKENS.TYPOGRAPHY.WEIGHT_EMPHASIS,
              color: MAGNETIC_TOKENS.COLORS.DEFAULT_TEXT_MEDIUM,
            })}
          >
            {totalCount}
          </span>
        </a>
      ) : (
        <div
          className={css(MAGNETIC_TOKENS.TYPOGRAPHY.ELLIPSES.END, {
            marginLeft: MAGNETIC_TOKENS.SPACING[3] * indentation,
            flex: '0 0 auto',
            maxWidth: '100%',
            fontSize: '14px',
            lineHeight: '20px',
            color: MAGNETIC_TOKENS.COLORS.DEFAULT_TEXT,
            textDecoration: 'none',
          })}
        >
          <Icon type={iconName} size="SMALL" className={{ verticalAlign: 'middle', marginRight: '0.5em' }} />
          {displayName}
          <span
            className={css('count', {
              marginLeft: MAGNETIC_TOKENS.SPACING[2],
              fontSize: MAGNETIC_TOKENS.TYPOGRAPHY.SIZE_P4,
              fontWeight: MAGNETIC_TOKENS.TYPOGRAPHY.WEIGHT_EMPHASIS,
              color: MAGNETIC_TOKENS.COLORS.DEFAULT_TEXT_MEDIUM,
            })}
          >
            {totalCount}
          </span>
        </div>
      )}

      <div className={css({ display: 'flex', flex: '0 0 auto', gap: MAGNETIC_TOKENS.SPACING[2], marginLeft: 'auto' })}>
        {isArray(healthGroups) &&
          healthGroups.map(({ health, count }) => (
            <SectionCardEntryTag
              key={health}
              entityType={entityType}
              health={health}
              count={count}
              href={createHref(entityType, globals, health)}
            />
          ))}
      </div>
      {/* {toggle} */}
    </div>
  );
}
